import { useMemo, useState, useCallback } from "react";
import {
  sortRows,
  applyColumnFilters,
  applyGlobalSearch,
} from "../lib/tableTools";

/**
 * Iter-69: useTableControls — combined sort/filter/search state for any
 * table. Pass in the raw row list + column config, get back the processed
 * rows plus handlers for the SortableTableHead / TableSearchInput components.
 *
 * Iter-70: added optional `defaultSort` so each page can opt into a sensible
 * initial sort (e.g. Inventory → Asset Tag, Reports → Date desc).
 *
 * @param {Array} rows - raw row data (after any external filtering you do)
 * @param {Array} columns - column config:
 *   { key, label, accessor, sortable?, filterable?, searchable?,
 *     filterFormatter?, searchFormatter? }
 * @param {object} [options]
 * @param {string} [options.defaultSortKey] - column key to sort by on mount
 * @param {"asc"|"desc"} [options.defaultSortDir] - direction (default "asc")
 *
 * accessor: string key OR function(row) => value
 * filterFormatter: (raw, row) => string — used to bucket distinct values
 * searchFormatter: (raw, row) => string — used to build searchable text
 *
 * @returns {object}
 *  - processedRows: rows after search + filters + sort
 *  - sortKey, sortDir: current sort state ("none" | "asc" | "desc")
 *  - toggleSort(columnKey): tri-state cycle none → asc → desc → none
 *  - filters: { [columnKey]: Set<string> } per-column selected values
 *  - setColumnFilter(columnKey, Set<string>): replace a column's filter
 *  - search, setSearch: global search string
 *  - clearAll(): reset all state
 *  - activeFilterCount: number of active per-column filters (for badge)
 */
export default function useTableControls(rows, columns, options = {}) {
  const { defaultSortKey = null, defaultSortDir = "asc" } = options;
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState(defaultSortKey ? defaultSortDir : "none");
  const [filters, setFilters] = useState({}); // { columnKey: Set }
  const [search, setSearch] = useState("");

  const toggleSort = useCallback((columnKey) => {
    setSortKey((prevKey) => {
      if (prevKey !== columnKey) {
        setSortDir("asc");
        return columnKey;
      }
      // Same column → cycle direction
      setSortDir((prevDir) => {
        if (prevDir === "asc") return "desc";
        if (prevDir === "desc") return "none";
        return "asc";
      });
      // When cycling to "none" we still keep key so the UI can show off-state
      return columnKey;
    });
  }, []);

  const setColumnFilter = useCallback((columnKey, valueSet) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (!valueSet || valueSet.size === 0) {
        delete next[columnKey];
      } else {
        next[columnKey] = valueSet;
      }
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setSortKey(defaultSortKey);
    setSortDir(defaultSortKey ? defaultSortDir : "none");
    setFilters({});
    setSearch("");
  }, [defaultSortKey, defaultSortDir]);

  const activeFilterCount = Object.keys(filters).length;

  const processedRows = useMemo(() => {
    let out = rows || [];
    out = applyGlobalSearch(out, columns, search);
    out = applyColumnFilters(out, columns, filters);
    if (sortKey && sortDir !== "none") {
      const col = columns.find((c) => c.key === sortKey);
      if (col) out = sortRows(out, col, sortDir);
    }
    return out;
  }, [rows, columns, search, filters, sortKey, sortDir]);

  return {
    processedRows,
    sortKey,
    sortDir,
    toggleSort,
    filters,
    setColumnFilter,
    search,
    setSearch,
    clearAll,
    activeFilterCount,
  };
}
